import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth as firebaseAuth } from '../../firebaseConfig';
import { motion } from 'framer-motion';
import { User, CreditCard, Star, Mail, MapPin, Calendar, Check, Sparkles, ArrowRight, ArrowUpRight, ExternalLink, AlertTriangle } from 'lucide-react';
import Navbar from '../../components/Navbar';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserFriendlyMessage } from '../../utils/errorMessages';
import { usePlan } from '../../contexts/PlanContext';
import Button from '../../components/ui/Button';

const PLAN_LABELS = { free: 'Gratuito', pro: 'Pro', premium: 'Premium' };

const PLANS_INFO = {
  free: {
    name: 'Gratuito', price: 'R$ 0,00', priceId: 'price_1TF1fz6AS5CKOwTBoaCz0WfB',
    features: ['1 Mapa Natal', 'Análise resumida por IA', '3 mensagens com IA por mês'],
  },
  pro: {
    name: 'Pro', price: 'R$ 29,90', priceId: 'price_1TF1g96AS5CKOwTB5W67hwPr',
    features: ['5 mapas astrais', 'Análise completa por IA', '20 mensagens com IA por mês'],
  },
  premium: {
    name: 'Premium', price: 'R$ 49,90', priceId: 'price_1TF1gK6AS5CKOwTB1LiXvCKT',
    features: ['Tudo do Pro', 'Mapas ilimitados', '50 mensagens com IA por mês'],
  },
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plan, limits, usage, period, hasSubscription, refreshPlan } = usePlan();
  const [activeTab, setActiveTab] = useState('dados');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasMap, setHasMap] = useState(null);
  const [subAction, setSubAction] = useState(null); // 'upgrading' | null
  const [subSuccess, setSubSuccess] = useState(null);
  const [upgradeConfirm, setUpgradeConfirm] = useState(null); // { priceId, planName, price } | null
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    birthDate: '',
    city: ''
  });

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const db = getFirestore();
        const userDoc = doc(db, 'users', user.uid);
        const userSnapshot = await getDoc(userDoc);
        if (userSnapshot.exists()) {
          const data = userSnapshot.data();
          const freshData = {
            name: data.nomeCompleto || '',
            email: data.email || '',
            birthDate: data.birthDate || '',
            city: data.city || ''
          };
          setUserData(freshData);
          localStorage.setItem(`user_${user.uid}`, JSON.stringify(freshData));
        }
      } catch (err) {
        // Fallback to cache if Firestore fetch fails
        const cachedUserData = localStorage.getItem(`user_${user.uid}`);
        if (cachedUserData) {
          setUserData(JSON.parse(cachedUserData));
        }
        console.error(err);
        setError(getUserFriendlyMessage(err));
      }
    };

    const checkUserMap = async () => {
      try {
        const db = getFirestore();
        const { getDocs, collection, where, query, limit } = await import('firebase/firestore');
        const q = query(
          collection(db, 'astrological_reports'),
          where('user_id', '==', user.uid),
          limit(1)
        );
        const snap = await getDocs(q);
        setHasMap(!snap.empty);
      } catch {
        setHasMap(null);
      }
    };

    if (user) {
      loadUserData();
      checkUserMap();
    }
  }, [user]);

  const handleOpenStripePortal = async () => {
    try {
      const fns = getFunctions();
      const createPortalSession = httpsCallable(fns, 'createPortalSession');
      const { data } = await createPortalSession({ returnUrl: `${window.location.origin}/profile` });
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error opening Stripe portal:', error);
      setError(getUserFriendlyMessage(error));
    }
  };

  const handleUpgradeConfirm = async () => {
    if (!upgradeConfirm) return;
    setSubAction('upgrading');
    setSubSuccess(null);
    setError(null);
    try {
      const fns = getFunctions();
      const upgradeFn = httpsCallable(fns, 'upgrade_subscription');
      await upgradeFn({ priceId: upgradeConfirm.priceId });
      const syncFn = httpsCallable(fns, 'sync_subscription');
      await syncFn();
      const maxAttempts = 10;
      for (let i = 0; i < maxAttempts; i++) {
        await firebaseAuth.currentUser.getIdToken(true);
        const { claims } = await firebaseAuth.currentUser.getIdTokenResult();
        if ((claims.stripeRole || 'free') !== plan) break;
        await new Promise(r => setTimeout(r, 1000));
      }
      await refreshPlan();
      setUpgradeConfirm(null);
      setSubSuccess('Plano atualizado com sucesso!');
    } catch (err) {
      setError(getUserFriendlyMessage(err));
    } finally {
      setSubAction(null);
    }
  };

  const handleCancel = async () => {
    // Redirect to Stripe Portal for cancellation
    handleOpenStripePortal();
  };

  const menuItems = [
    { icon: <User size={20} />, label: 'Dados Pessoais', tab: 'dados' },
    { icon: <CreditCard size={20} />, label: 'Assinatura', tab: 'assinatura' },
    { icon: <Star size={20} />, label: 'Meus Mapas', href: '/meus-mapas' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveSuccess(false);
    setError(null);
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'users', user.uid), {
        nomeCompleto: userData.name,
        email: userData.email,
        birthDate: userData.birthDate || null,
        city: userData.city || null,
      });
      localStorage.setItem(`user_${user.uid}`, JSON.stringify(userData));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(getUserFriendlyMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 text-gray-700">
          Você precisa estar autenticado para acessar esta página.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
      <Navbar />

      {error && (
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="bg-red-50 text-red-500 rounded-lg p-4">
            {error}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Profile Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-8 text-center border border-purple-100 relative overflow-hidden"
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 -translate-y-8 translate-x-8 w-64 h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
          <div className="absolute bottom-0 left-0 translate-y-8 -translate-x-8 w-64 h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>

          <div className="relative mx-auto w-32 h-32 mb-6">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-full w-full h-full flex items-center justify-center text-4xl shadow-lg">
              {userData.name.charAt(0).toUpperCase() || '✨'}
            </div>
          </div>
          
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
            {userData.name}
          </h1>
          <p className="text-gray-500 mb-3">{userData.email}</p>
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-6 ${
            plan === 'premium' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' :
            plan === 'pro' ? 'bg-purple-100 text-purple-700' :
            'bg-gray-100 text-gray-600'
          }`}>
            Plano {PLAN_LABELS[plan] || 'Gratuito'}
          </span>

        </motion.div>

        {/* Next Step Card */}
        {hasMap !== null && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`rounded-2xl shadow-lg p-6 border flex items-center gap-6 cursor-pointer hover:shadow-xl transition-shadow ${
              hasMap
                ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-100'
                : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border-transparent'
            }`}
            onClick={() => navigate(hasMap ? '/meus-mapas' : '/generate-map')}
          >
            <div className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 ${
              hasMap ? 'bg-purple-100' : 'bg-white/20'
            }`}>
              <Sparkles className={`w-7 h-7 ${hasMap ? 'text-purple-500' : 'text-white'}`} />
            </div>
            <div className="flex-1">
              <h3 className={`font-semibold text-lg ${hasMap ? 'text-gray-800' : 'text-white'}`}>
                {hasMap ? 'Explore seu mapa astral' : 'Gere seu primeiro mapa astral'}
              </h3>
              <p className={`text-sm ${hasMap ? 'text-gray-500' : 'text-white/80'}`}>
                {hasMap
                  ? 'Veja seus signos, planetas, casas e converse com a IA sobre seu mapa.'
                  : 'Descubra o que as estrelas revelam sobre sua personalidade, relacionamentos e futuro.'}
              </p>
            </div>
            <ArrowRight className={`w-6 h-6 shrink-0 ${hasMap ? 'text-purple-400' : 'text-white/70'}`} />
          </motion.div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
              <h2 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6 flex items-center gap-2">
                Menu
              </h2>
              <nav className="space-y-2">
                {menuItems.map((item, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <a
                      onClick={() => {
                        if (item.tab) setActiveTab(item.tab);
                        else if (item.href) navigate(item.href);
                      }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer ${
                        item.tab === activeTab
                          ? 'bg-purple-50 text-purple-600'
                          : 'text-gray-600 hover:bg-purple-50'
                      }`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </a>
                  </motion.div>
                ))}
              </nav>
            </div>
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-3 space-y-8"
          >
            {/* Tab: Dados Pessoais */}
            {activeTab === 'dados' && (
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                <h2 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6 flex items-center gap-2">
                  <User size={20} /> Dados Pessoais
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Nome Completo</label>
                      <div className="relative">
                        <input type="text" name="name" value={userData.name} onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 pl-10 text-gray-700" />
                        <User className="w-5 h-5 text-purple-400 absolute left-2 top-2.5" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">E-mail</label>
                      <div className="relative">
                        <input type="email" name="email" value={userData.email} onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 pl-10 text-gray-700" />
                        <Mail className="w-5 h-5 text-purple-400 absolute left-2 top-2.5" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Data de Nascimento</label>
                      <div className="relative">
                        <input type="date" name="birthDate" value={userData.birthDate} onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 pl-10 text-gray-700" />
                        <Calendar className="w-5 h-5 text-purple-400 absolute left-2 top-2.5" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Cidade</label>
                      <div className="relative">
                        <input type="text" name="city" value={userData.city} onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 pl-10 text-gray-700" />
                        <MapPin className="w-5 h-5 text-purple-400 absolute left-2 top-2.5" />
                      </div>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={saving}
                    loading={saving}
                    loadingText="Salvando..."
                    fullWidth
                    className={saveSuccess ? '!bg-green-500 !from-green-500 !to-green-500 !shadow-green-500/25' : ''}
                  >
                    {saveSuccess ? (
                      <><Check className="w-5 h-5" /> Salvo com sucesso!</>
                    ) : 'Salvar Alterações'}
                  </Button>
                </form>
              </div>
            )}

            {/* Tab: Assinatura */}
            {activeTab === 'assinatura' && (
              <div className="space-y-6">
                {/* Plano Atual */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                  <h2 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6 flex items-center gap-2">
                    <CreditCard size={20} /> Sua Assinatura
                  </h2>

                  {!hasSubscription ? (
                    <div className="text-center py-8">
                      <Sparkles className="w-12 h-12 text-purple-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">Nenhum plano ativo</h3>
                      <p className="text-gray-500 text-sm mb-6">Escolha um plano para desbloquear todos os recursos.</p>
                      <Button onClick={() => navigate('/pricing')}>
                        Ver Planos
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Current plan info */}
                      <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                              plan === 'premium' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' :
                              plan === 'pro' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {PLAN_LABELS[plan] || 'Gratuito'}
                            </span>
                            <span className="text-lg font-bold text-gray-800">
                              {PLANS_INFO[plan]?.price || 'R$ 0,00'}<span className="text-sm font-normal text-gray-500">/mês</span>
                            </span>
                          </div>
                          {period?.end && (
                            <p className="text-xs text-gray-500">
                              Próxima renovação: {new Date(period.end).toLocaleDateString('pt-BR')}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Usage */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">Mensagens IA</p>
                          <p className="text-sm font-semibold text-gray-800">
                            {usage.chat_messages} / {limits.chat_messages === Infinity ? 'ilimitado' : limits.chat_messages}
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">Mapas gerados</p>
                          <p className="text-sm font-semibold text-gray-800">
                            {usage.own_maps} / {limits.own_maps === Infinity ? 'ilimitado' : limits.own_maps}
                          </p>
                        </div>
                      </div>

                      {/* Success feedback */}
                      {subSuccess && (
                        <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm flex items-center gap-2 mb-4">
                          <Check className="w-4 h-4" /> {subSuccess}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Upgrade/Downgrade Options */}
                {hasSubscription && (
                  <div className="space-y-4">
                    {/* Upgrade options */}
                    {plan === 'free' && (
                      <>
                        <PlanCard info={PLANS_INFO.pro} label="Upgrade" onAction={() => setUpgradeConfirm({ priceId: PLANS_INFO.pro.priceId, planName: PLANS_INFO.pro.name, price: PLANS_INFO.pro.price })} loading={subAction === 'upgrading'} />
                        <PlanCard info={PLANS_INFO.premium} label="Upgrade" onAction={() => setUpgradeConfirm({ priceId: PLANS_INFO.premium.priceId, planName: PLANS_INFO.premium.name, price: PLANS_INFO.premium.price })} loading={subAction === 'upgrading'} />
                      </>
                    )}
                    {plan === 'pro' && (
                      <>
                        <PlanCard info={PLANS_INFO.premium} label="Upgrade" onAction={() => setUpgradeConfirm({ priceId: PLANS_INFO.premium.priceId, planName: PLANS_INFO.premium.name, price: PLANS_INFO.premium.price })} loading={subAction === 'upgrading'} />
                        <PlanCard info={PLANS_INFO.free} label="Downgrade" variant="outline" onAction={() => setUpgradeConfirm({ priceId: PLANS_INFO.free.priceId, planName: PLANS_INFO.free.name, price: PLANS_INFO.free.price })} loading={subAction === 'upgrading'} />
                      </>
                    )}
                    {plan === 'premium' && (
                      <>
                        <PlanCard info={PLANS_INFO.pro} label="Downgrade" variant="outline" onAction={() => setUpgradeConfirm({ priceId: PLANS_INFO.pro.priceId, planName: PLANS_INFO.pro.name, price: PLANS_INFO.pro.price })} loading={subAction === 'upgrading'} />
                        <PlanCard info={PLANS_INFO.free} label="Downgrade" variant="outline" onAction={() => setUpgradeConfirm({ priceId: PLANS_INFO.free.priceId, planName: PLANS_INFO.free.name, price: PLANS_INFO.free.price })} loading={subAction === 'upgrading'} />
                      </>
                    )}

                    {/* Portal + Cancel */}
                    <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                      <div className="flex items-center justify-between">
                        <button onClick={handleOpenStripePortal}
                          className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1 transition-colors">
                          <ExternalLink className="w-4 h-4" /> Gerenciar assinatura no Stripe
                        </button>
                        {plan !== 'free' && (
                          <button onClick={handleCancel}
                            className="text-sm text-red-400 hover:text-red-600 transition-colors">
                            Cancelar assinatura
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

          </motion.div>
        </div>
      </div>

      {/* Plan Change Confirmation Dialog */}
      {upgradeConfirm && (() => {
        const PLAN_ORDER = { free: 0, pro: 1, premium: 2 };
        const targetKey = Object.keys(PLANS_INFO).find(k => PLANS_INFO[k].priceId === upgradeConfirm.priceId);
        const isDowngrade = PLAN_ORDER[targetKey] < PLAN_ORDER[plan];
        const actionLabel = isDowngrade ? 'downgrade' : 'upgrade';
        return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !subAction && setUpgradeConfirm(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full border border-purple-100"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              Confirmar {actionLabel}
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              Deseja alterar seu plano para <strong>{upgradeConfirm.planName}</strong> por <strong>{upgradeConfirm.price}/mês</strong>?
              {isDowngrade
                ? ' A mudança será aplicada no próximo ciclo de cobrança.'
                : ' A cobrança será feita no cartão já cadastrado.'}
            </p>
            <div className="flex gap-3">
              <Button
                onClick={handleUpgradeConfirm}
                disabled={subAction === 'upgrading'}
                loading={subAction === 'upgrading'}
                loadingText="Atualizando..."
                className="flex-1"
              >
                {`Confirmar ${actionLabel}`}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setUpgradeConfirm(null)}
                disabled={subAction === 'upgrading'}
              >
                Cancelar
              </Button>
            </div>
          </motion.div>
        </div>
        );
      })()}
    </div>
  );
};

const PlanCard = ({ info, label, onAction, loading, variant }) => (
  <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100 flex items-center justify-between gap-4">
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="font-bold text-gray-800">{info.name}</h3>
        <span className="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          {info.price}<span className="text-xs font-normal text-gray-500">/mês</span>
        </span>
      </div>
      <ul className="space-y-1">
        {info.features.map((f, i) => (
          <li key={i} className="text-sm text-gray-500 flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5 text-purple-500 shrink-0" /> {f}
          </li>
        ))}
      </ul>
    </div>
    <Button
      variant={variant === 'outline' ? 'secondary' : 'primary'}
      size="sm"
      onClick={onAction}
      disabled={loading}
      loading={loading}
      className="shrink-0"
    >
      <ArrowUpRight className="w-4 h-4" /> {label}
    </Button>
  </div>
);

export default ProfilePage;